import { useState, useEffect, useMemo } from 'react'
import { X, ExternalLink, ArrowUpDown } from 'lucide-react'
import { presalesDashboardApi } from '@/api/client'
import { usePresalesDashboardStore } from '../store'
import type { KR3Demo } from '@/api/types'

interface KR3DemoDetailProps {
  isOpen: boolean
  onClose: () => void
}

type SortColumn = 'start_time' | 'event_summary' | 'deal_name' | 'presales_engineer' | 'duration_minutes'

export function KR3DemoDetailModal({ isOpen, onClose }: KR3DemoDetailProps) {
  const [demos, setDemos] = useState<KR3Demo[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [sortBy, setSortBy] = useState<SortColumn>('start_time')
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc')
  const [engineerFilter, setEngineerFilter] = useState<string>('all')
  const { timePeriod } = usePresalesDashboardStore()

  useEffect(() => {
    if (isOpen) {
      setIsLoading(true)
      setError(null)
      presalesDashboardApi.getKR3Demos({ period: timePeriod })
        .then(res => {
          if (res.success && res.data) {
            setDemos(res.data.demos)
          } else {
            setError(res.error || 'Failed to load demo data')
          }
        })
        .catch(err => setError(err.message || 'Failed to load demo data'))
        .finally(() => setIsLoading(false))
    }
  }, [isOpen])

  const engineers = useMemo(() => {
    const set = new Set<string>()
    demos.forEach(d => { if (d.presales_engineer) set.add(d.presales_engineer) })
    return Array.from(set).sort()
  }, [demos])

  const filteredDemos = useMemo(() => {
    if (engineerFilter === 'all') return demos
    return demos.filter(d => d.presales_engineer === engineerFilter)
  }, [demos, engineerFilter])

  const sortedDemos = useMemo(() => {
    return [...filteredDemos].sort((a, b) => {
      let cmp = 0
      switch (sortBy) {
        case 'start_time':
          cmp = (a.start_time || '').localeCompare(b.start_time || '')
          break
        case 'event_summary':
          cmp = (a.event_summary || '').localeCompare(b.event_summary || '')
          break
        case 'deal_name':
          cmp = (a.deal_name || '').localeCompare(b.deal_name || '')
          break
        case 'presales_engineer':
          cmp = (a.presales_engineer || '').localeCompare(b.presales_engineer || '')
          break
        case 'duration_minutes':
          cmp = (a.duration_minutes ?? 0) - (b.duration_minutes ?? 0)
          break
      }
      return sortDir === 'desc' ? -cmp : cmp
    })
  }, [filteredDemos, sortBy, sortDir])

  const handleSort = (col: SortColumn) => {
    if (sortBy === col) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc')
    } else {
      setSortBy(col)
      setSortDir('asc')
    }
  }

  if (!isOpen) return null

  const formatDate = (d: string | null) => {
    if (!d) return '-'
    return new Date(d).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: '2-digit' })
  }

  const formatTime = (d: string | null) => {
    if (!d) return ''
    return new Date(d).toLocaleTimeString('en-AU', {
      timeZone: 'Australia/Melbourne',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    })
  }

  const SortHeader = ({ column, children, className = '' }: { column: SortColumn; children: React.ReactNode; className?: string }) => (
    <th
      className={`pb-2 pr-3 cursor-pointer select-none hover:text-[var(--text-secondary)] transition-colors ${className}`}
      onClick={() => handleSort(column)}
    >
      <span className="inline-flex items-center gap-1">
        {children}
        {sortBy === column ? (
          <span className="text-primary">{sortDir === 'asc' ? '↑' : '↓'}</span>
        ) : (
          <ArrowUpDown className="h-3 w-3 opacity-30" />
        )}
      </span>
    </th>
  )

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" onClick={onClose}>
      <div className="absolute inset-0 bg-black/60" />
      <div className="relative z-10 w-full max-w-5xl mx-4 max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-xl shadow-2xl flex flex-col max-h-[85vh]">
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-3 border-b border-[var(--border-color)] flex-shrink-0">
            <div>
              <h3 className="text-sm font-semibold text-[var(--text-primary)]">KR3 — Demo Activity Breakdown</h3>
              <p className="text-xs text-[var(--text-muted)] mt-0.5">
                Demo events linked to deals (current quarter) &middot; {demos.length} total
              </p>
            </div>
            <button onClick={onClose} className="p-1 rounded hover:bg-[var(--bg-primary)] text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors">
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Filter pills */}
          {engineers.length > 1 && (
            <div className="px-5 py-3 border-b border-[var(--border-color)] flex items-center gap-2 flex-shrink-0">
              <button
                onClick={() => setEngineerFilter('all')}
                className={`px-2.5 py-1 text-xs rounded-full border transition-colors ${
                  engineerFilter === 'all'
                    ? 'bg-primary/10 border-primary/40 text-primary'
                    : 'border-[var(--border-color)] text-[var(--text-secondary)] hover:border-[var(--text-muted)]'
                }`}
              >
                All ({demos.length})
              </button>
              {engineers.map(eng => {
                const count = demos.filter(d => d.presales_engineer === eng).length
                return (
                  <button
                    key={eng}
                    onClick={() => setEngineerFilter(eng)}
                    className={`px-2.5 py-1 text-xs rounded-full border transition-colors ${
                      engineerFilter === eng
                        ? 'bg-primary/10 border-primary/40 text-primary'
                        : 'border-[var(--border-color)] text-[var(--text-secondary)] hover:border-[var(--text-muted)]'
                    }`}
                  >
                    {eng.split(' ')[0]} ({count})
                  </button>
                )
              })}
            </div>
          )}

          {/* Content */}
          <div className="overflow-auto flex-1 px-5 py-3">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
              </div>
            ) : error ? (
              <div className="text-center py-12 text-sm text-red-400">{error}</div>
            ) : sortedDemos.length === 0 ? (
              <div className="text-center py-12 text-sm text-[var(--text-muted)]">No demos found for this period.</div>
            ) : (
              <table className="w-full text-sm">
                <thead className="sticky top-0 z-10 bg-[var(--bg-secondary)]">
                  <tr className="text-left text-xs font-medium text-[var(--text-muted)] uppercase tracking-wider border-b border-[var(--border-color)]">
                    <SortHeader column="start_time">Date</SortHeader>
                    <SortHeader column="event_summary">Meeting</SortHeader>
                    <SortHeader column="deal_name">Deal</SortHeader>
                    <SortHeader column="presales_engineer">Engineer</SortHeader>
                    <SortHeader column="duration_minutes" className="text-right">Duration</SortHeader>
                    <th className="pb-2 w-8"></th>
                  </tr>
                </thead>
                <tbody>
                  {sortedDemos.map((demo, idx) => (
                    <tr key={`${demo.event_id}-${demo.hubspot_deal_id}-${idx}`} className="border-b border-[var(--border-color)] hover:bg-[var(--bg-primary)] transition-colors">
                      <td className="py-2 pr-3 whitespace-nowrap">
                        <div className="text-[var(--text-primary)]">{formatDate(demo.start_time)}</div>
                        <div className="text-xs text-[var(--text-muted)]">{formatTime(demo.start_time)}</div>
                      </td>
                      <td className="py-2 pr-3 max-w-[200px]">
                        <div className="truncate text-[var(--text-primary)]">{demo.event_summary}</div>
                      </td>
                      <td className="py-2 pr-3 max-w-[200px]">
                        <div className="truncate font-medium text-[var(--text-primary)]">{demo.deal_name}</div>
                        {demo.company_name && (
                          <div className="text-xs text-[var(--text-muted)]">{demo.company_name}</div>
                        )}
                      </td>
                      <td className="py-2 pr-3 text-[var(--text-muted)]">{demo.presales_engineer || '-'}</td>
                      <td className="py-2 pr-3 text-right text-[var(--text-muted)]">
                        {demo.duration_minutes ? `${demo.duration_minutes}m` : '-'}
                      </td>
                      <td className="py-2">
                        <a
                          href={`https://app.hubspot.com/contacts/22499506/deal/${demo.hubspot_deal_id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[var(--text-muted)] hover:text-primary transition-colors"
                          title="Open deal in HubSpot"
                        >
                          <ExternalLink className="h-3.5 w-3.5" />
                        </a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
